import humanizeDuration from 'humanize-duration';
import { max, min, sortBy } from 'lodash';
import memoize from 'lodash/memoize';
import round from 'lodash/round';
import type { Duration, DurationUnit } from 'luxon';
import { DateTime, Settings } from 'luxon';

// eslint-disable-next-line no-restricted-imports
import { TODAY } from 'helpers/dateMovingConstants';
import type { ISOTime, MonthKey, MonthKeyOrMonthKeyRange } from 'types/datetime';

Settings.defaultZone = 'utc';
const MONTH_KEY_FORMAT = 'yyyy-MM';
const MONTH_KEY_REGEX = /^\d\d\d\d-\d\d$/;
const JANUARY_MONTH = 1;
const DECEMBER_MONTH = 12;

// We call this a lot and the results rarely change. Doing any sort of date
// math is really slow and this becomes a problem with the amount that we do it
// for rollup formula evaluation.
export const getMonthKeysForRange = memoize(
  (startMonth: DateTime, endMonth: DateTime): MonthKey[] => {
    return getMonthsBetweenDates(startMonth, endMonth).map(getMonthKey);
  },
  (startMonth: DateTime, endMonth: DateTime) => `${startMonth.toMillis()}-${endMonth.toMillis()}`,
);

export const getMonthKeysForYearTo = (fiscalYearStartMonth: number, now: DateTime): MonthKey[] => {
  let yearStart = now.set({ month: fiscalYearStartMonth });

  if (now < yearStart) {
    yearStart = yearStart.minus({ years: 1 });
  }

  return getMonthKeysForRange(yearStart, now);
};

export const getMonthKeysForQuarterTo = (
  fiscalYearStartMonth: number,
  now: DateTime,
): MonthKey[] => {
  const quarterStartMonths = getQuarterStarts(fiscalYearStartMonth).sort((a, b) => a - b);
  const currentMonth = now.month;
  const quarterStartMonth =
    quarterStartMonths.findLast((m) => currentMonth >= m) ??
    quarterStartMonths[quarterStartMonths.length - 1];
  const months = (currentMonth - quarterStartMonth + 12) % 12;
  const startOfQuarter = now.minus({ months });
  return getMonthKeysForRange(startOfQuarter, now);
};

export const getMonthKeysForQTD = memoize((fiscalYearStartMonth: number): MonthKey[] => {
  return getMonthKeysForQuarterTo(fiscalYearStartMonth, TODAY);
});

/** QLC: Quarter to Last Close */
export const getMonthKeysForQLC = memoize(
  (fiscalYearStartMonth: number, lastActualsTime: string): MonthKey[] => {
    const lastActuals = DateTime.fromISO(lastActualsTime);
    return getMonthKeysForQuarterTo(fiscalYearStartMonth, lastActuals);
  },
);

export const getMonthKeysForTrailingMonths = memoize(
  (numberOfTrailingMonths: number, now: DateTime): MonthKey[] => {
    // getMonthKeysForRange is inclusive of the end date, so we need to go back one month more
    const start = now.minus({ months: numberOfTrailingMonths - 1 });
    return getMonthKeysForRange(start, now);
  },
  (fiscalYearStartMonth: number, now: DateTime) => `${fiscalYearStartMonth}-${now.toMillis()}`,
);

export const getMonthKeysForYLC = memoize(
  (fiscalYearStartMonth: number, lastActualsTime: DateTime): MonthKey[] => {
    return getMonthKeysForYearTo(fiscalYearStartMonth, lastActualsTime);
  },
  (fiscalYearStartMonth: number, lastActualsTime: DateTime) =>
    `${fiscalYearStartMonth}-${lastActualsTime.toMillis()}`,
);

/** HYTD: Half Year To Date */
export const getMonthKeysForHYTD = memoize(
  (fiscalYearStartMonth: number, now: DateTime): MonthKey[] => {
    const midYearMonth =
      fiscalYearStartMonth + 6 > 12 ? fiscalYearStartMonth + 6 - 12 : fiscalYearStartMonth + 6;
    const halfYearStart = DateTime.local(now.year, midYearMonth, 1);

    let fiscalYearStart;
    const currentMonth = now.month;
    if (currentMonth < fiscalYearStartMonth) {
      // fiscal year is in the previous year
      fiscalYearStart = DateTime.local(now.year - 1, fiscalYearStartMonth, 1);
    } else {
      fiscalYearStart = DateTime.local(now.year, fiscalYearStartMonth, 1);
    }

    const monthKeysFromFiscal = getMonthKeysForRange(fiscalYearStart, now);
    const monthKeysFromHalfYear = getMonthKeysForRange(halfYearStart, now);

    if (
      monthKeysFromFiscal.length < monthKeysFromHalfYear.length ||
      monthKeysFromHalfYear.length === 0
    ) {
      return monthKeysFromFiscal;
    }
    return monthKeysFromHalfYear;
  },
  (fiscalYearStartMonth: number, now: DateTime) => `${fiscalYearStartMonth}-${now.toMillis()}`,
);

export const isEndOfQuarter = (dt: DateTime, fiscalYearStartMonth: number) => {
  return getQuarterEnds(fiscalYearStartMonth).includes(dt.month);
};

export const isEndOfYear = (dt: DateTime, fiscalYearStartMonth: number) => {
  const fyEndMonth =
    fiscalYearStartMonth === JANUARY_MONTH ? DECEMBER_MONTH : fiscalYearStartMonth - 1;
  return dt.month === fyEndMonth;
};

export const extractMonthKey = (time: string): MonthKey => {
  return time.slice(0, 7);
};

export const getCurrentMonthKey = (): MonthKey => extractMonthKey(new Date().toISOString());

// Parsing datetimes is actually fairly expensive and these functions are
// easily memoized. There is a small set of inputs and they don't often change
// but are used a lot.
export const getMonthKey = memoize(
  (month: DateTime): MonthKey => {
    return month.toFormat(MONTH_KEY_FORMAT);
  },
  (month: DateTime) => month.toMillis().toString(),
);

export const getMonthKeyFromISO = memoize((iso: string): MonthKey => {
  return getMonthKey(DateTime.fromISO(iso));
});

/**
 * Returns the DateTime object for the given month key
 * @param monthKey
 * @example
 * getDateTimeFromMonthKey('2020-01')
 */
export const getDateTimeFromMonthKey = memoize((monthKey: MonthKey): DateTime => {
  return DateTime.fromFormat(monthKey, MONTH_KEY_FORMAT);
});

export function monthKeyRange(from: MonthKey, to: MonthKey): MonthKey[] {
  const [first, last] = from < to ? [from, to] : [to, from];

  let curr = first;
  const res: MonthKey[] = [curr];
  while (curr !== last) {
    curr = nextMonthKey(curr);
    res.push(curr);
  }
  return res;
}

// Given a list of month keys, reduces it to a list of month keys or month key ranges, where consecutive
// month keys are combined into ranges.
export function toMonthKeyOrRangeList(monthKeys: MonthKey[]): MonthKeyOrMonthKeyRange[] {
  if (monthKeys.length === 0) {
    return [];
  }

  const monthKeyDateTimes = monthKeys.map(getDateTimeFromMonthKey);
  const sortedMonthKeyDateTimes = sortBy(monthKeyDateTimes);
  const sortedMonthKeys = sortedMonthKeyDateTimes.map(getMonthKey);

  const result: MonthKeyOrMonthKeyRange[] = [];
  let rangeStart = sortedMonthKeys[0];
  let rangeEnd = sortedMonthKeys[0];

  for (let i = 1; i < sortedMonthKeys.length; i++) {
    const currentMonthKey = sortedMonthKeys[i];

    if (areMonthKeysConsecutive(rangeEnd, currentMonthKey)) {
      rangeEnd = currentMonthKey;
    } else {
      if (rangeStart === rangeEnd) {
        result.push({ type: 'month', monthKey: rangeStart });
      } else {
        result.push({ type: 'range', from: rangeStart, to: rangeEnd });
      }

      rangeStart = currentMonthKey;
      rangeEnd = currentMonthKey;
    }
  }

  if (rangeStart === rangeEnd) {
    result.push({ type: 'month', monthKey: rangeStart });
  } else {
    result.push({ type: 'range', from: rangeStart, to: rangeEnd });
  }

  return result;
}

export function formatMonthKeyOrRange(monthKeyOrRange: MonthKeyOrMonthKeyRange): string {
  if (monthKeyOrRange.type === 'month') {
    return shortMonthFormat(getDateTimeFromMonthKey(monthKeyOrRange.monthKey));
  }

  const fromMonth = shortMonthFormat(getDateTimeFromMonthKey(monthKeyOrRange.from));
  const toMonth = shortMonthFormat(getDateTimeFromMonthKey(monthKeyOrRange.to));

  return `${fromMonth} - ${toMonth}`;
}

export const getNumMonthsInclusive = (start: MonthKey, end: MonthKey): number => {
  const startTime = getDateTimeFromMonthKey(start);
  const endTime = getDateTimeFromMonthKey(end);
  return endTime.diff(startTime, 'months').months + 1;
};

const SHORT_MONTH_WITH_DAY_AND_TIME_FORMAT = 'MMM d %yy hh:mm a';
const SHORT_MONTH_WITH_DAY_FORMAT = 'MMM d %yy';
const SHORT_MONTH_FORMAT = 'MMM %yy';
const SHORT_QUARTER_FORMAT = 'Qq %yy';
export const shortMonthWithDayAndTimeFormat = memoize((date: DateTime): string => {
  return date.toFormat(SHORT_MONTH_WITH_DAY_AND_TIME_FORMAT).replace('%', `'`);
});

export const shortMonthWithDayFormat = memoize((date: DateTime): string => {
  return date.toFormat(SHORT_MONTH_WITH_DAY_FORMAT).replace('%', `'`);
});

export const shortMonthFormat = memoize((date: DateTime): string => {
  return date.toFormat(SHORT_MONTH_FORMAT).replace('%', `'`);
});

export const shortQuarterFormat = (date: DateTime): string => {
  return date.toFormat(SHORT_QUARTER_FORMAT).replace('%', `'`);
};

function getQuarterStarts(fiscalYearStartMonth: number) {
  const quarterStart = fiscalYearStartMonth;
  return [
    quarterStart,
    (quarterStart + 3) % 12,
    (quarterStart + 6) % 12,
    (quarterStart + 9) % 12,
  ].map((m) => (m === 0 ? 12 : m));
}

function getQuarterEnds(fiscalYearStartMonth: number) {
  const lastQuarterEnd =
    fiscalYearStartMonth === JANUARY_MONTH ? DECEMBER_MONTH : fiscalYearStartMonth - 1;
  return [
    (lastQuarterEnd + 3) % 12,
    (lastQuarterEnd + 6) % 12,
    (lastQuarterEnd + 9) % 12,
    lastQuarterEnd,
  ].map((m) => (m === 0 ? 12 : m));
}

// When labelByNextYear is null/undefined (user hasn't set the toggle), we fall back to legacy
// defaults that vary by call site. Quarter labels and annual rollup columns historically used
// end-of-year labels, while some month-key annual paths used start-of-year.
function getDisplayFiscalYear(
  baseYear: number,
  labelByNextYear: boolean | null | undefined,
  defaultToEndOfYear: boolean,
): number {
  if (labelByNextYear === true) return baseYear + 1;
  if (labelByNextYear === false) return baseYear;
  return defaultToEndOfYear ? baseYear + 1 : baseYear;
}

export const shortFiscalQuarterFormat = (
  date: DateTime,
  fiscalYearStartMonth: number,
  labelByNextYear?: boolean | null,
): string => {
  const quarterStartMonths = getQuarterStarts(fiscalYearStartMonth);
  const sortedQuarterStarts = quarterStartMonths.toSorted((a, b) => a - b);
  const quarterStartMonth =
    sortedQuarterStarts.findLast((m) => date.month >= m) ??
    sortedQuarterStarts[sortedQuarterStarts.length - 1];
  const quarterStartIndex = quarterStartMonths.indexOf(quarterStartMonth);
  const isCalendarYear = fiscalYearStartMonth === 1;
  const fiscalYearStart = date.month < fiscalYearStartMonth ? date.year - 1 : date.year;
  const displayYear = isCalendarYear
    ? date.year
    : getDisplayFiscalYear(fiscalYearStart, labelByNextYear, true);
  const yearPrefix = isCalendarYear ? '' : 'FY ';
  return `Q${quarterStartIndex + 1} ${yearPrefix}'${String(displayYear).slice(-2)}`;
};

export const shortYearFormat = (
  date: DateTime,
  fiscalYearStartMonth?: number,
  labelByNextYear?: boolean | null,
): string => {
  if (fiscalYearStartMonth == null || fiscalYearStartMonth === 1) {
    return `FY ${date.year}`;
  }
  const fyStartYear = date.month < fiscalYearStartMonth ? date.year - 1 : date.year;
  return `FY ${getDisplayFiscalYear(fyStartYear, labelByNextYear, false)}`;
};

const getFromShortMonthWithDay = memoize((dateStr: string): DateTime => {
  return DateTime.fromFormat(dateStr.replace(`'`, '%'), SHORT_MONTH_WITH_DAY_FORMAT);
});

const getFromShortMonth = memoize((dateStr: string): DateTime => {
  return DateTime.fromFormat(dateStr.replace(`'`, '%'), SHORT_MONTH_FORMAT);
});

const getFromShortQuarter = memoize((dateStr: string): DateTime => {
  return DateTime.fromFormat(dateStr.replace(`'`, '%'), SHORT_QUARTER_FORMAT);
});

export const getMonthsBetweenDates = (start: DateTime, end: DateTime): DateTime[] => {
  const res: DateTime[] = [];

  let curr = start;
  while (curr <= end) {
    res.push(curr);
    curr = curr.plus({ months: 1 });
  }
  return res;
};

export const getMonthsBetweenMonths = (start: MonthKey, end: MonthKey): MonthKey[] => {
  const res: MonthKey[] = [];
  let curr = start;
  while (curr <= end) {
    res.push(curr);
    curr = nextMonthKey(curr);
  }
  return res;
};

export const numMonthsBetweenDates = (start: DateTime, end: DateTime): number => {
  return round(end.diff(start, 'months').months);
};

export const nextMonthKey = memoize((mk: MonthKey): MonthKey => {
  const [year, month] = mk.split('-');
  if (Number(month) === 12) {
    return `${String(Number(year) + 1).padStart(4, '0')}-01`;
  }

  return `${year}-${String(Number(month) + 1).padStart(2, '0')}`;
});

const areMonthKeysConsecutive = (monthKeyA: MonthKey, monthKeyB: MonthKey): boolean => {
  return nextMonthKey(monthKeyA) === monthKeyB;
};

export const previousMonthKey = memoize((mk: MonthKey): MonthKey => {
  const [year, month] = mk.split('-');
  if (Number(month) === 1) {
    return `${String(Number(year) - 1).padStart(4, '0')}-12`;
  }

  return `${year}-${String(Number(month) - 1).padStart(2, '0')}`;
});

export const offsetMonthKey = (offset: number, mk: MonthKey): MonthKey => {
  let remaining = Math.abs(offset);
  let curr = mk;
  while (remaining > 0) {
    curr = offset > 0 ? nextMonthKey(curr) : previousMonthKey(curr);
    remaining -= 1;
  }
  return curr;
};

const isDateEqualOrBefore = (date: DateTime, otherDate: DateTime): boolean => {
  return date <= otherDate;
};

export const isDateBetween = (date: MonthKey, start: MonthKey, end: MonthKey): boolean => {
  const parsedStart = getDateTimeFromMonthKey(start);
  const parsedEnd = getDateTimeFromMonthKey(end);
  const parsedDate = getDateTimeFromMonthKey(date);

  return isDateEqualOrBefore(parsedStart, parsedDate) && isDateEqualOrBefore(parsedDate, parsedEnd);
};

export const getStartAndEndMonthKeys = (
  monthKeys: MonthKey[],
): { startMonthKey: MonthKey | undefined; endMonthKey: MonthKey | undefined } => {
  const startMonthKey = min(monthKeys);
  const endMonthKey = max(monthKeys);

  return { startMonthKey, endMonthKey };
};

export const getMonthKeyRange = memoize(
  (startMonthKey: MonthKey, endMonthKey: MonthKey): MonthKey[] => {
    if (startMonthKey > endMonthKey) {
      const err = Error(`startMonthKey must be before endMonthKey`);
      return [];
    }

    const keys: MonthKey[] = [startMonthKey];
    let mk = startMonthKey;
    while (mk !== endMonthKey) {
      mk = nextMonthKey(mk);
      keys.push(mk);
    }

    return keys;
  },
  (startMonthKey: MonthKey, endMonthKey: MonthKey) => `${startMonthKey},${endMonthKey}`,
);

/**
 * Converts an ISO string with ms to one without, for communication with the API
 * @param isoStringOrDateTime an ISO string or DateTime that may contain ms
 * @returns an ISO string without ms
 */
export const getISOTimeWithoutMs = (isoStringOrDateTime: ISOTime | DateTime): string | null => {
  return (
    typeof isoStringOrDateTime === 'string'
      ? DateTime.fromISO(isoStringOrDateTime)
      : isoStringOrDateTime
  )
    .startOf('second')
    .toISO({ suppressMilliseconds: true });
};

/**
 * Formats a DateTime to an ISO time without ms, for communication with the API
 * @param dateTime a DateTime object
 * @returns an ISO string without ms
 */
export const formatISOWithoutMs = (dateTime: DateTime): string | null =>
  dateTime.startOf('second').toISO({ suppressMilliseconds: true });

/**
 * Formats a MonthKey to an ISO time without ms, for communication with the API
 * @param monthKey a MonthKey string
 * @returns an ISO string without ms
 */

export const getISOTimeWithoutMsFromMonthKey = memoize(
  (monthKey: MonthKey) => formatISOWithoutMs(getDateTimeFromMonthKey(monthKey)),
  (monthKey: MonthKey) => monthKey,
);

export function isValidMonthKey(str: string) {
  return MONTH_KEY_REGEX.test(str);
}

function getQuarter(date: DateTime) {
  return Math.floor((date.month - 1) / 3 + 1);
}

function getQuarterStartMonth(quarter: number, year?: number) {
  if (year == null) {
    year = DateTime.now().year;
  }
  return DateTime.fromObject({ year, month: 3 * (quarter - 1) + 1 });
}

function getQuarterEndMonth(quarter: number, year?: number) {
  if (year == null) {
    year = DateTime.now().year;
  }
  return DateTime.fromObject({ year, month: 3 * quarter }).endOf('month');
}

export { TODAY };
export const START_OF_MONTH = TODAY.startOf('month');
export const CURRENT_MONTH_KEY = getMonthKey(TODAY);
export const START_OF_LAST_MONTH = TODAY.minus({ months: 1 }).startOf('month');
export const LAST_MONTH_MONTH_KEY = getMonthKey(START_OF_LAST_MONTH);

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});

export const humanizeDurationShort = (duration: Duration): string => {
  return shortEnglishHumanizer(duration.toMillis(), {
    largest: 1,
    round: true,
    spacer: '',
  });
};

export const prettyPrintTimestamp = (dateString: string): string => {
  return DateTime.fromISO(dateString)
    .setZone('local')
    .toFormat("LLL dd `yy 'at' hh:mm a")
    .replace('`', "'");
};

export const getDiffFromNow = (isoString: string): Duration => {
  return DateTime.fromISO(isoString).diffNow().rescale();
};

// Add more as necessary
const DATE_PARSE_ATTEMPT_FORMATS = [
  'M/yy',
  'M/yyyy',
  'MM/yy',
  'MM/yyyy',
  'MMM/yy',
  'MMM/yyyy',
  'MMMM/yy',
  'MMMM/yyyy',

  'yy/M',
  'yyyy/M',
  'yy/MM',
  'yyyy/MM',
  'yy/MMM',
  'yyyy/MMM',
  'yy/MMMM',
  'yyyy/MMMM',

  'M-yy',
  'M-yyyy',
  'MM-yy',
  'MM-yyyy',
  'MMM-yy',
  'MMM-yyyy',
  'MMMM-yy',
  'MMMM-yyyy',

  'yy-M',
  'yyyy-M',
  'yy-MM',
  'yyyy-MM',
  'yy-MMM',
  'yyyy-MMM',
  'yy-MMMM',
  'yyyy-MMMM',

  'MMM yy',
  'MMM yyyy',
  'MMM %yy',
  'MMMM yy',
  'MMMM yyyy',
  'MMMM %yy',

  'MMM d yy',
  'MMM d %yy',
  'MMM d yyyy',
  'MMMM d yy',
  'MMMM d %yy',
  'MMMM d yyyy',

  'MMM dd yy',
  'MMM dd %yy',
  'MMM dd yyyy',
  'MMMM dd yy',
  'MMMM dd %yy',
  'MMMM dd yyyy',

  'M/d/yy',
  'M/d/yyyy',
  'M/dd/yy',
  'M/dd/yyyy',
  'MM/d/yy',
  'MM/d/yyyy',
  'MM/dd/yy',
  'MM/dd/yyyy',
  'M-d-yy',
  'M-d-yyyy',
  'M-dd-yy',
  'M-dd-yyyy',
  'MM-d-yy',
  'MM-d-yyyy',
  'MM-dd-yy',
  'MM-dd-yyyy',

  'd/M/yy',
  'd/M/yyyy',
  'dd/M/yy',
  'dd/M/yyyy',
  'd/MM/yy',
  'd/MM/yyyy',
  'dd/MM/yy',
  'dd/MM/yyyy',
  'd-M-yy',
  'd-M-yyyy',
  'dd-M-yy',
  'dd-M-yyyy',
  'd-MM-yy',
  'd-MM-yyyy',
  'dd-MM-yy',
  'dd-MM-yyyy',

  'yy/M/d',
  'yyyy/M/d',
  'yy/M/dd',
  'yyyy/M/dd',
  'yy/MM/d',
  'yyyy/MM/d',
  'yy/MM/dd',
  'yyyy/MM/dd',
  'yy-M-d',
  'yyyy-M-d',
  'yy-M-dd',
  'yyyy-M-dd',
  'yy-MM-d',
  'yyyy-MM-d',
  'yy-MM-dd',
  'yyyy-MM-dd',

  'D',
  'DD',
  'DDD',

  'Qq yy',
  'Qq %yy',
  'Qq yyyy',
];

// Try Runway specific formats first and then fall back to some common formats
// and finally fall back to fromISO.
const DT_PARSE_ATTEMPTS = [
  (raw: string) => (isValidMonthKey(raw) ? getDateTimeFromMonthKey(raw) : null),
  getFromShortMonthWithDay,
  getFromShortMonth,
  getFromShortQuarter,
  ...DATE_PARSE_ATTEMPT_FORMATS.map(
    (f) => (raw: string) => DateTime.fromFormat(raw.replace(`'`, '%'), f),
  ),
  (raw: string) => DateTime.fromISO(raw),
];

// TODO: I have tried a couple libraries that can parse many different date
// formats but they haven't had good (or any) built in types and don't work
// well in some common cases. If this solution proves to be too difficult to
// maintain or make flexible, we could consider trying to build something that
// does more intelligent pattern matching.
export const inferDate = (raw: string): DateTime | undefined => {
  let dt: DateTime | undefined;

  DT_PARSE_ATTEMPTS.some((fn) => {
    const attempted = fn(raw);
    if (attempted?.isValid && attempted.toUnixInteger() > 0) {
      dt = attempted;
      return true;
    }
    return false;
  });

  return dt;
};

export const inferDateForAttribute = (raw: string): DateTime | undefined => {
  // inferDate infers some numbers as dates (e.g. '10' as <today at 10am>, '1970' as <1970-01-01>, '197001' as
  // <1970-01-01>)
  // So we just return undefined for all numbers
  const isAllDigits = raw.match(/^\d+$/);
  if (isAllDigits != null) {
    return undefined;
  }
  return inferDate(raw);
};

export const MONTHS = [
  '', // placeholder so that months indexes line up nicely
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const ALL_DATE_FN_UNITS = ['d', 'm', 'w', 'q', 'y'] as const;
type DateFnUnit = (typeof ALL_DATE_FN_UNITS)[number];

function isDateFnUnit(unit: string): unit is DateFnUnit {
  return ALL_DATE_FN_UNITS.includes(unit as DateFnUnit);
}

export const DATE_FN_UNIT_ARG_IDX = 2;

export const DATE_FN_UNIT_TO_DURATION_UNIT: Record<DateFnUnit, DurationUnit> = {
  d: 'days',
  m: 'months',
  w: 'weeks',
  q: 'quarters',
  y: 'years',
};

export function getLuxonUnitFromText(text: string): DurationUnit | null {
  const lower = text.toLowerCase();
  if (!isDateFnUnit(lower)) {
    return null;
  }
  return DATE_FN_UNIT_TO_DURATION_UNIT[lower];
}

const DATE_LITERAL_FORMAT = 'yyyy-MM-dd';
export function parseLiteralDateString(literal: string): DateTime | null {
  const d = DateTime.fromFormat(literal, DATE_LITERAL_FORMAT);
  return d.isValid ? d : null;
}

export function toLiteralDateString(date: DateTime): string {
  return date.toFormat(DATE_LITERAL_FORMAT);
}

export function weekendMaskStrInvalidReason(weekendMaskStr: string): string | null {
  if (weekendMaskStr.length === 0) {
    return null;
  }
  if (weekendMaskStr.length !== 7) {
    return 'Weekend string must have a lengh of 7';
  }

  if (weekendMaskStr.match(/[^01]/)) {
    return 'Weekend string contains non 0 or 1 characters';
  }

  return null;
}

export function workDaysBetweenDates(
  start: DateTime,
  end: DateTime,
  weekendMask: boolean[],
  holidays: DateTime[],
) {
  let workdays = 0;
  let curr = start;

  while (curr < end) {
    // Check if it's a work day
    if (!weekendMask[curr.weekday - 1]) {
      // If so, we need to also check if it's a holiday
      if (!holidays.find((holiday) => curr.hasSame(holiday, 'day'))) {
        workdays++;
      }
    }

    curr = curr.plus({ days: 1 });
  }

  return workdays;
}

export const getFinancialQuarter = memoize(
  (offset: number, monthKey: MonthKey, isEnd?: boolean): string => {
    const thisMonthDateTime = getDateTimeFromMonthKey(monthKey);
    const quarter = getQuarter(thisMonthDateTime);
    const quarterBoundMonthFn = isEnd ? getQuarterEndMonth : getQuarterStartMonth;
    return getMonthKey(
      quarterBoundMonthFn(quarter, thisMonthDateTime.year).plus({ month: 3 * offset }),
    );
  },
  (offset: number, monthKey: MonthKey, isEnd?: boolean) => [offset, monthKey, isEnd].join('.'), // delimiter is not in month keys
);

export const getFinancialYear = memoize(
  (offset: number, monthKey: MonthKey, isEnd?: boolean): string => {
    const baseTime = getDateTimeFromMonthKey(monthKey);
    const endOrStartAdjusted = isEnd ? baseTime.endOf('year') : baseTime.startOf('year');
    const result = endOrStartAdjusted.plus({ year: offset });
    return getMonthKey(result);
  },
  (offset: number, monthKey: MonthKey, isEnd?: boolean) => [offset, monthKey, isEnd].join('.'),
);

export function combineDateRanges(
  dateRanges: Array<[MonthKey, MonthKey]>,
): Array<[MonthKey, MonthKey]> {
  const combinedDateRanges: Array<[MonthKey, MonthKey]> = [];

  // Sort by start
  const sortedDateRanges = dateRanges.sort((a, b) => a[0].localeCompare(b[0]));

  let currentRange: [MonthKey, MonthKey] | null = null;
  sortedDateRanges.forEach((dateRange) => {
    if (currentRange == null) {
      currentRange = dateRange;
      return;
    }

    // if `dateRange` overlaps with `currentRange` at all, combine them
    // and continue to the next range. If they are disjoint, push
    // `currentRange`, make `dateRange` the new `currentRange` and
    // continue.
    if (dateRange[0] <= currentRange[1]) {
      currentRange = [
        currentRange[0],
        dateRange[1].localeCompare(currentRange[1]) < 0 ? currentRange[1] : dateRange[1],
      ];
    } else {
      combinedDateRanges.push(currentRange);
      currentRange = dateRange;
    }
  });

  if (currentRange != null) {
    combinedDateRanges.push(currentRange);
  }

  return combinedDateRanges;
}

/** Returns the number of years between two dates, rounded to the nearest year. */
export const getYearDifference = (start: DateTime, end: DateTime): number => {
  const yearsDelta = start.diff(end, 'years').years;
  return Math.abs(Math.round(yearsDelta));
};

export const formatIsoStringToLocalTime = (isoString: string | undefined, format: string) => {
  const dateTime =
    isoString != null
      ? DateTime.fromISO(isoString).minus({ minutes: new Date().getTimezoneOffset() })
      : undefined;
  return dateTime != null ? dateTime.toFormat(format) : undefined;
};

let formatter: Intl.DateTimeFormat | null = null;
function getFormatter() {
  if (formatter == null) {
    formatter = new Intl.DateTimeFormat(window.navigator.language, {
      dateStyle: 'medium',
      timeZone: 'UTC',
    });
  }
  return formatter;
}

export function formatDate(date: number | string | Date | null): string {
  let dateToDisplay = date;
  if (!(dateToDisplay instanceof Date)) {
    if (dateToDisplay == null) {
      return '';
    }
    dateToDisplay = new Date(dateToDisplay);
  }

  try {
    return getFormatter().format(dateToDisplay);
  } catch (error) {
    if (error instanceof RangeError) {
      return 'Invalid Date';
    }
    throw error;
  }
}
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function isMonthKeyAfter(monthKeyA: MonthKey, monthKeyB: MonthKey): boolean {
  const dateA = getDateTimeFromMonthKey(monthKeyA);
  const dateB = getDateTimeFromMonthKey(monthKeyB);
  return dateA > dateB;
}
